import { Controller } from 'stimulus'

// Runs a Help Center walkthrough (app/training/*.yml, Walkthrough) over the
// real screens. Start walkthrough on a card (help/_walkthrough_card) hands
// this controller the tour; it lives on the layout's page wrapper, keeps its
// place in sessionStorage, and picks up again on every page load.
//
// Each point names a page (controller#action plus optional params, matched
// against data-walkthrough-page/-params on the wrapper) and a data-tour
// anchor on it:
// - Look points (until: next) explain and wait for "Got it, next".
// - Your turn points wait for the action: click the anchor, type in it
//   (input), or work inside it and press "Done, next" (done).
// While a point shows, clicks anywhere else are stopped, so a record can't be
// opened early and a step can't be skipped. End in the bar always gets out.
const STORE = 'limsey-walkthrough'
const WAIT_FOR_ANCHOR_MS = 4000
const WAIT_FOR_OPTIONAL_MS = 1200
const NAVIGATION_GRACE_MS = 2500
// Widgets that draw outside the element they belong to, plus our own UI and
// the Learn more drawer: clicks there always go through.
// Pop-up parts of form widgets (TomSelect and select2 dropdowns, date
// pickers). While one belonging to the highlighted spot is open, the
// highlight grows to include it, so a dropdown isn't cut off by the dimming.
const POPUPS = '.ts-dropdown, .select2-container--open .select2-dropdown, .flatpickr-calendar.open'
// A "fill in" point whose target goes away (its window closed) counts as
// done after this long.
const GONE_MEANS_DONE_MS = 800
// A Look, click, or type point whose spot is gone this long (its window
// closed, the page re-rendered without it) steps back to one that's there.
const GONE_MEANS_LOST_MS = 1500
// After a click on the same page, how long to wait before showing the next
// point, so a click that reloads the page doesn't flash it first.
const AFTER_CLICK_MS = 300
// Page pop-up windows a highlighted spot can sit inside (the job status
// editor). The callout keeps clear of the whole window, not just the spot.
const POPUP_WINDOWS = '[data-popup-editor]'
const ALWAYS_ALLOWED = [
  '.walkthrough-callout', '.walkthrough-bar', '.help-drawer', '.help-drawer-scrim',
  '.select2-container', '.select2-dropdown', '.flatpickr-calendar', '.ts-dropdown', '.ui-datepicker'
].join(', ')

export default class extends Controller {
  connect() {
    this.guardClick = this.guard.bind(this)
    this.watchTyping = this.typed.bind(this)
    this.ownClick = this.keepOwnClick.bind(this)
    this.beforeCache = () => this.clear()
    this.leaving = () => this.cancelGrace()
    document.addEventListener('click', this.guardClick, true)
    document.addEventListener('mousedown', this.guardClick, true)
    document.addEventListener('input', this.watchTyping, true)
    document.addEventListener('change', this.watchTyping, true)
    this.element.addEventListener('click', this.ownClick)
    this.element.addEventListener('mousedown', this.ownClick)
    window.addEventListener('beforeunload', this.leaving)
    document.addEventListener('turbolinks:before-visit', this.leaving)
    document.addEventListener('turbolinks:before-cache', this.beforeCache)
    this.render()
  }

  disconnect() {
    document.removeEventListener('click', this.guardClick, true)
    document.removeEventListener('mousedown', this.guardClick, true)
    document.removeEventListener('input', this.watchTyping, true)
    document.removeEventListener('change', this.watchTyping, true)
    this.element.removeEventListener('click', this.ownClick)
    this.element.removeEventListener('mousedown', this.ownClick)
    window.removeEventListener('beforeunload', this.leaving)
    document.removeEventListener('turbolinks:before-visit', this.leaving)
    document.removeEventListener('turbolinks:before-cache', this.beforeCache)
    this.cancelGrace()
    this.clear()
  }

  // ---------- starting and ending ----------

  start(event) {
    const button = event.currentTarget
    let tour
    try {
      tour = JSON.parse(button.dataset.walkthroughTour)
    } catch (e) {
      return
    }
    this.save({
      key: button.dataset.walkthroughKey,
      title: button.dataset.walkthroughTitle,
      help: button.dataset.walkthroughHelp,
      anchor: button.dataset.walkthroughAnchor,
      complete: button.dataset.walkthroughComplete,
      tour,
      i: 0,
      url: window.location.href
    })
    this.render()
  }

  end() {
    this.cancelGrace()
    this.forget()
    this.clear()
  }

  // ---------- state ----------

  load() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORE))
    } catch (e) {
      return null
    }
  }

  save(state) {
    try {
      window.sessionStorage.setItem(STORE, JSON.stringify(state))
    } catch (e) {
      // Storage refused (private window): the walkthrough ends at the next page.
    }
  }

  forget() {
    try {
      window.sessionStorage.removeItem(STORE)
    } catch (e) {
      // Nothing stored, nothing to remove.
    }
  }

  get page() {
    return this.data.get('page')
  }

  get pageParams() {
    try {
      return JSON.parse(this.data.get('params') || '{}')
    } catch (e) {
      return {}
    }
  }

  onPage(point) {
    if (!point || point.page !== this.page) return false
    const current = this.pageParams
    return Object.entries(point.params || {}).every(([key, value]) => current[key] === String(value))
  }

  // ---------- rendering ----------

  render(note, checkRequired = false) {
    this.clear()
    const state = this.load()
    if (!state) return
    if (state.finished) {
      this.showFinished(state)
      return
    }

    const point = state.tour[state.i]
    if (!point) {
      this.finish(state)
      return
    }

    if (!this.onPage(point)) {
      this.recover(state)
      return
    }

    state.url = window.location.href
    state.expectLeave = false
    this.save(state)
    // Set when the window of the point before closed (targetGone).
    const closed = this.windowClosed
    this.windowClosed = null
    this.renderToken = (this.renderToken || 0) + 1
    const token = this.renderToken
    const patience = point.optional || closed ? WAIT_FOR_OPTIONAL_MS : WAIT_FOR_ANCHOR_MS
    this.waitForAnchor(point.point, patience, (target) => {
      if (token !== this.renderToken) return
      if (target) {
        if (state.saved) {
          delete state.saved
          this.save(state)
        }
        this.show(state, point, target, note)
        if (checkRequired && point.until === 'done') this.markMissing()
      } else if (point.optional || closed === 'saved' || state.saved) {
        // Saved from inside its window (the window closed, or its form
        // reloaded the page): the window's step and its save button are
        // gone because they're done.
        state.i += 1
        this.save(state)
        this.render()
      } else if (closed === 'closed' && this.previousPoint(state) !== null) {
        // Closed without saving (its ×, Cancel): back to what opened it.
        state.i = this.previousPoint(state)
        this.save(state)
        this.render('The window closed before it was saved, so the walkthrough went back a step.')
      } else {
        this.showMissing(state)
      }
    })
  }

  // The current point isn't on this page. Work out why.
  recover(state) {
    const again = { ...state }
    // A form submitted with Enter lands on a later point's page.
    const ahead = this.pointAheadOnThisPage(state)
    if (ahead !== null) {
      again.i = ahead
      this.save(again)
      this.render()
    } else if (state.expectLeave && state.i > 0 && this.onPage(state.tour[state.i - 1])) {
      // A click that should have left the page came back to it (a failed
      // save re-renders the form): back to where the fields can be fixed.
      again.i = this.formPointBefore(state)
      again.expectLeave = false
      this.save(again)
      this.render('Something on this page needs fixing first. Check the messages and any highlighted fields.', true)
    } else if (this.pointBehindOnThisPage(state) !== null) {
      // Back to a page an earlier step belongs to (the browser's Back
      // button): pick up at the latest step on it.
      again.i = this.pointBehindOnThisPage(state)
      again.expectLeave = false
      this.save(again)
      this.render()
    } else {
      this.showOffPath(state)
    }
  }

  pointBehindOnThisPage(state) {
    for (let j = state.i - 1; j >= 0; j -= 1) {
      if (this.onPage(state.tour[j])) return j
    }
    return null
  }

  // The step Back goes to: the one before, on this page, stepping over any
  // step whose spot isn't here now (an optional step skipped on the way
  // forward, a window that has since closed). Null when there's none; the
  // browser's Back button covers earlier pages.
  previousPoint(state) {
    let j = state.i - 1
    while (j >= 0 && this.onPage(state.tour[j])) {
      if (this.findAnchor(state.tour[j].point)) return j
      j -= 1
    }
    return null
  }

  back() {
    const state = this.load()
    if (!state) return
    const j = this.previousPoint(state)
    if (j === null) return
    this.cancelGrace()
    state.i = j
    state.expectLeave = false
    this.save(state)
    this.render()
  }

  // The fill-in-the-form point on this page before the current one (the step
  // where every field can be edited), or the point just before it.
  formPointBefore(state) {
    for (let j = state.i - 1; j >= 0; j -= 1) {
      const point = state.tour[j]
      if (!this.onPage(point)) break
      if (point.until === 'done') return j
    }
    return state.i - 1
  }

  pointAheadOnThisPage(state) {
    const last = Math.min(state.tour.length, state.i + 4)
    for (let j = state.i + 1; j < last; j += 1) {
      if (this.onPage(state.tour[j])) return j
    }
    return null
  }

  waitForAnchor(name, patience, callback) {
    const started = Date.now()
    const look = () => {
      const target = this.findAnchor(name)
      if (target || Date.now() - started > patience) {
        callback(target)
      } else {
        this.pollTimer = setTimeout(look, 150)
      }
    }
    look()
  }

  // Every on-screen element carrying the anchor. Most anchors are one
  // element; a table column (data-tour on its header and each of its cells)
  // is several, highlighted together as one area.
  findAnchors(name) {
    return Array.from(document.querySelectorAll(`[data-tour="${CSS.escape(name)}"]`)).filter(el => this.visible(el))
  }

  // The first anchor with this name that is actually on screen: an empty or
  // hidden element (a guided-questions box with no questions) doesn't count.
  findAnchor(name) {
    const candidates = document.querySelectorAll(`[data-tour="${CSS.escape(name)}"]`)
    return Array.from(candidates).find(el => this.visible(el)) || null
  }

  show(state, point, target, note) {
    const look = point.until === 'next'
    this.current = { state, point, target, look, targets: this.findAnchors(point.point) }

    this.bar = this.el('div', 'walkthrough-bar')
    this.bar.innerHTML = `<span>Walkthrough: <b></b></span><span class="walkthrough-dots">${
      state.tour.map((_, index) => `<i class="${index <= state.i ? 'on' : ''}"></i>`).join('')
    }</span><span>${state.i + 1} of ${state.tour.length}</span>`
    this.bar.querySelector('b').textContent = state.title
    this.bar.appendChild(this.button('End', 'walkthrough-end', () => this.end()))

    this.shade = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    this.shade.setAttribute('class', `walkthrough-shade ${look ? 'is-look' : 'is-turn'}`)
    this.shadePath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    this.shadePath.setAttribute('fill-rule', 'evenodd')
    this.shade.appendChild(this.shadePath)
    this.element.appendChild(this.shade)
    this.rings = []
    this.ringClass = `walkthrough-ring ${look ? 'is-look' : 'is-turn'}`

    this.callout = this.el('div', 'walkthrough-callout')
    const head = this.el('div', 'walkthrough-callout-head', this.callout)
    this.el('span', null, head).textContent = `Step ${state.i + 1} of ${state.tour.length}`
    const kind = this.el('span', `walkthrough-kind ${look ? 'is-look' : 'is-turn'}`, head)
    kind.textContent = look ? 'Look' : 'Your turn'
    this.el('p', null, this.callout).textContent = point.say
    if (!look) this.el('div', 'walkthrough-do', this.callout).textContent = point.do
    if (point.until === 'done') {
      const labels = this.requiredRows(target).map(field => field.label)
      if (labels.length) {
        const required = this.el('div', 'walkthrough-required', this.callout)
        required.textContent = `Required here: ${labels.join(', ')}`
      }
    }
    this.note = this.el('div', 'walkthrough-note', this.callout)
    this.note.textContent = note || ''
    this.note.hidden = !note
    const row = this.el('div', 'walkthrough-callout-row', this.callout)
    const actions = this.el('div', 'walkthrough-callout-links', row)
    if (this.previousPoint(state) !== null) actions.appendChild(this.button('Back', 'walkthrough-back', () => this.back()))
    actions.appendChild(this.learnMore(state))
    if (look) row.appendChild(this.button('Got it, next', 'walkthrough-primary', () => this.advance()))
    if (point.until === 'done') row.appendChild(this.button('Done, next', 'walkthrough-primary', () => this.finishForm()))

    // Clicks (guard) and typing (typed) on the spot are watched from the
    // document, so every element of a several-part anchor counts and a spot
    // the page re-renders keeps working.

    // A folded section (a collapsible task section) is unfolded, so what the
    // point describes can be seen.
    const folds = target.tagName === 'DETAILS' ? [target] : Array.from(target.querySelectorAll(':scope > details'))
    folds.forEach((fold) => { const node = fold; if (!node.open) node.open = true })

    const rect = target.getBoundingClientRect()
    if (rect.top < 60 || rect.bottom > window.innerHeight - 40) {
      // A tall area (the rest of a form) starts at its top rather than its middle.
      target.scrollIntoView({ block: rect.height > window.innerHeight - 120 ? 'start' : 'center' })
    }
    this.lastPlacement = null
    this.track()
  }

  // Keeps the ring and callout on the target while the page changes under
  // it: a guided question appearing above, a sample row pushing a section
  // down, scrolling, resizing. Checked every frame (one getBoundingClientRect)
  // and moved only when something changed. A target re-rendered by the page
  // (the samples table refreshing) is found again and re-attached; one that
  // is briefly gone hides the ring and callout until it's back.
  track() {
    // One loop at a time: follow() can end this point and show another,
    // which starts its own loop, so this one stops when that happens.
    this.loop = (this.loop || 0) + 1
    const { loop } = this
    const tick = () => {
      if (!this.current || loop !== this.loop) return
      this.follow()
      if (loop === this.loop) this.frame = requestAnimationFrame(tick)
    }
    this.frame = requestAnimationFrame(tick)
  }

  follow() {
    let { target } = this.current
    if (!this.visible(target)) {
      const again = this.findAnchor(this.current.point.point)
      if (again && again !== target) {
        this.current.target = again
        target = again
      }
    }
    const shown = this.visible(target)
    this.shade.style.display = shown ? '' : 'none'
    this.rings.forEach((ring) => { const node = ring; node.hidden = !shown })
    this.callout.hidden = !shown
    if (!shown) {
      this.targetGone()
      return
    }
    this.goneSince = null

    const others = this.findAnchors(this.current.point.point)
    this.current.targets = others.length ? others : [target]
    const placement = this.boxes().concat([{ top: window.innerWidth, left: window.innerHeight }])
      .map(box => [box.top, box.left, box.width, box.height].map(Math.round).join(':')).join(',')
    if (placement === this.lastPlacement) return
    this.lastPlacement = placement
    this.position()
  }

  // The spot went away and stayed away. A "fill in" point in a window the
  // user closed (the trait picker's Close button instead of Done): closing it
  // means they're done with it; the next point then knows whether the window
  // was saved (a submit inside it) or just closed. Any other point: the screen moved on without
  // it (a window closed, a section re-rendered), so step back to the latest
  // earlier point that is still on screen, or say the screen changed.
  targetGone() {
    if (!this.goneSince) {
      this.goneSince = Date.now()
      return
    }
    const waited = Date.now() - this.goneSince
    if (this.current.point.until === 'done') {
      if (waited > GONE_MEANS_DONE_MS) {
        this.goneSince = null
        this.windowClosed = this.current.submitted ? 'saved' : 'closed'
        this.advance()
      }
    } else if (waited > GONE_MEANS_LOST_MS) {
      this.goneSince = null
      this.lost()
    }
  }

  lost() {
    const state = this.load()
    if (!state) return
    const j = this.previousPoint(state)
    if (j === null) {
      this.render()
      return
    }
    state.i = j
    state.expectLeave = false
    this.save(state)
    this.render('The screen changed, so the walkthrough stepped back to here.')
  }

  // The target's box, grown to take in any of its pop-ups that are open.
  // The area to highlight: every element of the anchor (one, or a whole
  // column), grown to take in any of their pop-ups that are open.
  focusRect() {
    const targets = this.current.targets && this.current.targets.length ? this.current.targets : [this.current.target]
    const clipped = targets.map(el => this.clippedRect(el)).filter(Boolean)
    const boxes = clipped.length ? clipped : [targets[0].getBoundingClientRect()]
    targets.forEach((target) => {
      const popups = Array.from(target.querySelectorAll(POPUPS))
      if (target.querySelector('select, input')) popups.push(...document.querySelectorAll(POPUPS))
      popups.forEach((popup) => { if (this.visible(popup)) boxes.push(popup.getBoundingClientRect()) })
    })
    const top = Math.min(...boxes.map(box => box.top))
    const left = Math.min(...boxes.map(box => box.left))
    const right = Math.max(...boxes.map(box => box.right))
    const bottom = Math.max(...boxes.map(box => box.bottom))
    return {
      top, left, right, bottom, width: right - left, height: bottom - top
    }
  }

  // An element's box, cut down to what its scrolling containers show (a
  // column in a table that scrolls sideways, a row in a scrolling list), or
  // null when it's scrolled out of sight. The containers are looked up once
  // per element.
  clippedRect(el) {
    if (!this.clipCache) this.clipCache = new WeakMap()
    let clips = this.clipCache.get(el)
    if (!clips) {
      clips = []
      // Containers above a fixed element (a modal window) don't cut it.
      let node = window.getComputedStyle(el).position === 'fixed' ? null : el.parentElement
      for (; node && node !== document.body; node = node.parentElement) {
        const style = window.getComputedStyle(node)
        if (/(auto|scroll|hidden)/.test(style.overflowX + style.overflowY)) clips.push(node)
        if (style.position === 'fixed') break
      }
      this.clipCache.set(el, clips)
    }
    let { top, left, bottom, right } = el.getBoundingClientRect()
    clips.forEach((node) => {
      const box = node.getBoundingClientRect()
      top = Math.max(top, box.top)
      left = Math.max(left, box.left)
      bottom = Math.min(bottom, box.bottom)
      right = Math.min(right, box.right)
    })
    if (bottom - top < 1 || right - left < 1) return null
    return {
      top, left, bottom, right, width: right - left, height: bottom - top
    }
  }

  visible(el) {
    if (!el || !el.isConnected) return false
    const rect = el.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  // ---------- required fields on a form step ----------

  // Done, next on a form step: only once every required field has a value.
  finishForm() {
    if (this.markMissing().length) return
    this.advance()
  }

  // Outlines the empty required fields and names them in the callout.
  markMissing() {
    if (!this.current) return []
    const missing = this.requiredRows(this.current.target).filter(field => !this.filled(field.row))
    this.current.target.querySelectorAll('.walkthrough-missing').forEach(el => el.classList.remove('walkthrough-missing'))
    this.current.target.classList.remove('walkthrough-missing')
    missing.forEach(field => field.row.classList.add('walkthrough-missing'))
    if (missing.length && this.note) {
      this.note.textContent = `Fill in the required ${missing.length === 1 ? 'field' : 'fields'} first: ${missing.map(field => field.label).join(', ')}.`
      this.note.hidden = false
      this.nudge(false)
      missing[0].row.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
    return missing
  }

  unmarkFilled() {
    if (!this.current) return
    const marked = Array.from(this.current.target.querySelectorAll('.walkthrough-missing'))
    if (this.current.target.classList.contains('walkthrough-missing')) marked.push(this.current.target)
    marked.forEach((row) => {
      if (this.filled(row)) row.classList.remove('walkthrough-missing')
    })
    const stillMissing = marked.some(row => row.classList.contains('walkthrough-missing'))
    if (marked.length && !stillMissing && this.note) this.note.hidden = true
  }

  // Required fields inside the target, one per form row: rows whose label
  // carries the red asterisk (JobsHelper#required_label, used by every job
  // field on the create-job form) plus any control marked required. Areas
  // marked data-walkthrough-skip-required (guided questions, which enforce
  // themselves) are left out.
  requiredRows(target) {
    const rows = new Map()
    const add = (row, labelEl) => {
      if (!row || rows.has(row)) return
      const label = ((labelEl && labelEl.textContent) || '').replace('*', '').replace(/\s+/g, ' ').trim()
      rows.set(row, label || 'a required field')
    }
    target.querySelectorAll('span.text-red-600').forEach((star) => {
      if (star.textContent.trim() !== '*' || star.closest('[data-walkthrough-skip-required]')) return
      const labelEl = star.closest('label, p, div')
      add(this.fieldRow(labelEl, target), labelEl)
    })
    target.querySelectorAll('input[required], select[required], textarea[required]').forEach((control) => {
      if (control.disabled || control.type === 'hidden' || control.closest('[data-walkthrough-skip-required]')) return
      const row = this.fieldRow(control, target)
      add(row, row && row.querySelector('label'))
    })
    return Array.from(rows, ([row, label]) => ({ row, label }))
  }

  // The form row holding an element: the nearest ancestor (inside the
  // target) holding both a label and an input, so a field found by its
  // asterisk and by its required input lands on the same row.
  fieldRow(el, target) {
    let node = el
    while (node) {
      if (node.querySelector && node.querySelector('input:not([type=hidden]), select, textarea')
          && node.querySelector('label, .text-red-600')) return node
      if (node === target) break // the target itself can be the row (the Name step)
      node = node.parentElement
    }
    return null
  }

  filled(row) {
    const controls = Array.from(row.querySelectorAll('input, select, textarea'))
      .filter(control => !control.disabled && !['hidden', 'submit', 'button'].includes(control.type))
    if (!controls.length) return true
    return controls.some((control) => {
      if (control.type === 'checkbox' || control.type === 'radio') return control.checked
      if (control.type === 'file') return control.files && control.files.length > 0
      if (control.tagName === 'SELECT') return Array.from(control.selectedOptions).some(option => option.value !== '')
      return (control.value || '').trim() !== ''
    })
  }

  // Moves to the next point. When that point is on another page, the action
  // is expected to navigate; if nothing happens (a required field stopped the
  // form), the walkthrough steps back and says so.
  advance(afterClick = false) {
    const state = this.load()
    if (!state) return
    // One action moves one point: drop this point's callout now, so more
    // keystrokes or clicks on the same target don't skip ahead.
    this.clear()
    const next = state.tour[state.i + 1]
    const leaving = next ? !this.onPage(next) : false
    state.i += 1
    if (state.i >= state.tour.length) {
      this.finish(state)
      return
    }

    state.expectLeave = leaving
    this.save(state)
    if (!leaving) {
      // After a click it waits a moment, and leaving the page cancels it, so
      // a click that reloads the page doesn't flash the next point first.
      this.graceTimer = setTimeout(() => this.render(), afterClick ? AFTER_CLICK_MS : 60)
      return
    }
    this.graceTimer = setTimeout(() => {
      const again = this.load()
      if (!again || again.i !== state.i) return
      again.i = this.formPointBefore(again)
      again.expectLeave = false
      this.save(again)
      this.render('Nothing happened yet. Something on this page still needs filling in.', true)
    }, NAVIGATION_GRACE_MS)
  }

  cancelGrace() {
    clearTimeout(this.graceTimer)
  }

  finish(state) {
    const done = { ...state, finished: true }
    this.save(done)
    this.showFinished(done)
    const token = document.querySelector('meta[name="csrf-token"]')
    fetch(done.complete, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { Accept: 'application/json', 'X-CSRF-Token': token ? token.content : '' }
    })
      .then(response => (response.ok ? response.json() : null))
      .then((json) => { if (json) this.updateBadges(json.badge) })
      .catch(() => {})
  }

  updateBadges(count) {
    Array.from(document.querySelectorAll('[data-help-badge]')).forEach((badge) => {
      const node = badge
      if (count > 0) node.textContent = count
      else node.remove()
    })
  }

  // ---------- docked callouts (not pointing at anything) ----------

  showFinished(state) {
    const callout = this.docked('Walkthrough done', 'is-done')
    this.el('p', null, callout).textContent = `${state.title} is ticked off in the Help Center.`
    const row = this.el('div', 'walkthrough-callout-row', callout)
    const back = this.el('a', 'walkthrough-primary', row)
    back.textContent = 'Back to the Help Center'
    back.href = this.data.get('helpCenter')
    back.addEventListener('click', () => this.forget())
    row.appendChild(this.button('Close', 'walkthrough-secondary', () => this.end()))
  }

  showOffPath(state) {
    const callout = this.docked('Walkthrough paused', 'is-way')
    this.el('p', null, callout).textContent = `You've left the ${state.title} walkthrough. Go back to where you were, or end it.`
    const row = this.el('div', 'walkthrough-callout-row', callout)
    const back = this.el('a', 'walkthrough-primary', row)
    back.textContent = 'Go back'
    back.href = state.url
    row.appendChild(this.button('End', 'walkthrough-secondary', () => this.end()))
  }

  showMissing(state) {
    const callout = this.docked('This screen changed', 'is-way')
    this.el('p', null, callout).textContent = "The walkthrough can't find its next spot on this page. The guide covers it instead."
    const row = this.el('div', 'walkthrough-callout-row', callout)
    row.appendChild(this.learnMore(state, 'Open the guide'))
    row.appendChild(this.button('Skip this step', 'walkthrough-secondary', () => this.advance()))
    row.appendChild(this.button('End', 'walkthrough-secondary', () => this.end()))
  }

  docked(label, tone) {
    this.callout = this.el('div', 'walkthrough-callout')
    this.el('div', `walkthrough-callout-head ${tone}`, this.callout).textContent = label
    this.callout.style.top = '4rem'
    this.callout.style.right = '1rem'
    return this.callout
  }

  // ---------- blocking everything but the highlighted spot ----------

  guard(event) {
    if (!this.current) return
    const { target } = event
    if (!(target instanceof Element)) return
    if (target.closest(ALWAYS_ALLOWED)) return
    const inTarget = this.inTarget(target)
    if (!this.current.look && inTarget) {
      if (event.type === 'click' && this.current.point.until === 'done') {
        const button = target.closest('button, input[type=submit]')
        if (button && button.form && button.type === 'submit') {
          this.current.submitted = true
          const state = this.load()
          if (state) this.save({ ...state, saved: true })
        }
      }
      // Right away, before the page's own handlers: a rails-ujs link
      // (method: :patch) submits a hidden form with a click of its own, which
      // must not meet this point's blocking.
      if (event.type === 'click' && this.current.point.until === 'click') this.advance(true)
      return
    }
    if (this.implicitSubmit(event)) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    if (event.type === 'click') this.nudge()
  }

  inTarget(node) {
    return (this.current.targets || [this.current.target]).some(el => el.contains(node))
  }

  // Enter in a highlighted field submits its form by "clicking" the form's
  // submit button, which may sit outside the highlight. That click (no mouse
  // behind it, so detail 0) goes through when the form holds the spot.
  implicitSubmit(event) {
    if (this.current.look || event.type !== 'click' || event.detail !== 0) return false
    const button = event.target.closest('button, input[type=submit]')
    if (!button || !button.form || (button.type || 'submit') !== 'submit') return false
    const { form } = button
    return (this.current.targets || [this.current.target]).some(el => form.contains(el) || el.contains(form))
  }

  // Typing in the spot: an "input" point moves on once there's something in
  // it; a "done" point takes the red outline off a field once it's filled.
  typed(event) {
    if (!this.current || this.current.look || !(event.target instanceof Element)) return
    if (!this.inTarget(event.target)) return
    if (this.current.point.until === 'input' && event.type === 'input') {
      if ((event.target.value || '').trim()) this.advance()
    } else if (this.current.point.until === 'done') {
      this.unmarkFilled()
    }
  }

  // The callout's and bar's own buttons stay ours: a page script closing its
  // pop-up on any click elsewhere (the Jobs screen's filter pickers) doesn't
  // see "Got it, next" or Back.
  keepOwnClick(event) {
    if (event.target instanceof Element && event.target.closest('.walkthrough-callout button, .walkthrough-bar button')) {
      event.stopPropagation()
    }
  }

  nudge(explain = true) {
    if (!this.callout || !this.note) return
    if (explain) {
      this.note.textContent = this.current.look
        ? 'Read this, then click Got it, next.'
        : 'Only the highlighted spot works during the walkthrough. End it from the blue bar.'
    }
    this.note.hidden = false
    this.callout.classList.remove('is-shaking')
    this.callout.getBoundingClientRect() // restart the animation
    this.callout.classList.add('is-shaking')
  }

  // ---------- layout ----------

  // The boxes to highlight. One box around the whole anchor, grown to take in
  // open pop-ups; except an anchor that is several separate choices (every
  // job name on the Jobs screen), which gets a box around each one on screen.
  // A table column (header and cells, all TH/TD) stays one box.
  boxes() {
    const targets = this.current.targets && this.current.targets.length ? this.current.targets : [this.current.target]
    const separate = targets.length > 1 && !targets.every(el => el.tagName === 'TD' || el.tagName === 'TH')
    if (!separate) return [this.focusRect()]
    const onScreen = targets.map(el => this.clippedRect(el))
      .filter(box => box && box.bottom > 0 && box.top < window.innerHeight)
    return onScreen.length ? onScreen : [targets[0].getBoundingClientRect()]
  }

  position() {
    if (!this.current || !this.shade || !this.callout) return
    const pad = 4
    const holes = this.boxes().map(box => ({
      top: box.top - pad, left: box.left - pad, width: box.width + pad * 2, height: box.height + pad * 2
    }))

    // The dimming: the whole screen, minus a hole per box.
    const viewW = window.innerWidth
    const viewH = window.innerHeight
    this.shade.setAttribute('viewBox', `0 0 ${viewW} ${viewH}`)
    this.shadePath.setAttribute('d', [`M0 0H${viewW}V${viewH}H0Z`]
      .concat(holes.map(h => `M${h.left} ${h.top}h${h.width}v${h.height}h${-h.width}Z`)).join(' '))

    // An outline per box.
    while (this.rings.length < holes.length) this.rings.push(this.el('div', this.ringClass))
    while (this.rings.length > holes.length) this.rings.pop().remove()
    holes.forEach((hole, index) => {
      Object.assign(this.rings[index].style, {
        top: `${hole.top}px`,
        left: `${hole.left}px`,
        width: `${hole.width}px`,
        height: `${hole.height}px`
      })
    })

    // The callout goes by the area all the boxes cover, so with several
    // (every job name) it sits beside them rather than over the next ones,
    // plus the pop-up window the spot is in, if any.
    const around = holes.slice()
    const popupWindow = this.current.target.closest(POPUP_WINDOWS)
    if (popupWindow) {
      const box = popupWindow.getBoundingClientRect()
      around.push({
        top: box.top - pad, left: box.left - pad, width: box.width + pad * 2, height: box.height + pad * 2
      })
    }
    const top0 = Math.min(...around.map(h => h.top)) + pad
    const left0 = Math.min(...around.map(h => h.left)) + pad
    const right0 = Math.max(...around.map(h => h.left + h.width)) - pad
    const bottom0 = Math.max(...around.map(h => h.top + h.height)) - pad
    const rect = {
      top: top0,
      left: left0,
      width: right0 - left0,
      height: bottom0 - top0,
      right: right0,
      bottom: bottom0
    }

    const width = this.callout.offsetWidth
    const height = this.callout.offsetHeight
    const gap = 12
    let top
    let left
    if (rect.left < 280 && rect.width < 280) {
      // The left menu: beside the item.
      left = rect.right + gap
      top = rect.top - 8
    } else if (rect.bottom + gap + height <= viewH - 8) {
      top = rect.bottom + gap
      left = rect.width > width ? rect.left + 16 : rect.left + rect.width / 2 - width / 2
    } else if (rect.top - gap - height >= 56) {
      top = rect.top - height - gap
      left = rect.width > width ? rect.left + 16 : rect.left + rect.width / 2 - width / 2
    } else if (rect.right + gap + width <= viewW - 8) {
      // No room above or below (a tall window): beside it instead of over it.
      left = rect.right + gap
      top = Math.max(rect.top, 56)
    } else if (rect.left - gap - width >= 8) {
      left = rect.left - gap - width
      top = Math.max(rect.top, 56)
    } else {
      // No room on any side (a tall table). If the spot and the callout fit
      // one above the other once the spot is scrolled to the top, do that
      // (once per point); the next frame places the callout below it.
      const fits = rect.height + gap + height + 16 + 64 <= viewH
      if (fits && holes.length === 1 && !this.current.scrolledToFit) {
        this.current.scrolledToFit = true
        this.current.target.scrollIntoView({ block: 'start' })
        return
      }
      top = viewH - height - 8
      left = rect.left + 16
    }
    left = Math.max(8, Math.min(left, viewW - width - 8))
    top = Math.max(56, Math.min(top, viewH - height - 8))
    this.callout.style.top = `${top}px`
    this.callout.style.left = `${left}px`
  }

  clear() {
    clearTimeout(this.pollTimer)
    cancelAnimationFrame(this.frame)
    this.loop = (this.loop || 0) + 1
    if (this.current) {
      this.current.target.querySelectorAll('.walkthrough-missing').forEach(el => el.classList.remove('walkthrough-missing'))
      this.current.target.classList.remove('walkthrough-missing')
    }
    this.goneSince = null
    this.current = null
    const nodes = [this.bar, this.shade, this.callout].concat(this.rings || [])
    nodes.forEach((node) => { if (node) node.remove() })
    this.bar = null
    this.shade = null
    this.rings = []
    this.callout = null
    this.note = null
  }

  // ---------- small builders ----------

  el(tag, className, parent = this.element) {
    const node = document.createElement(tag)
    if (className) node.className = className
    parent.appendChild(node)
    return node
  }

  button(label, className, onClick) {
    const node = document.createElement('button')
    node.type = 'button'
    node.className = className
    node.textContent = label
    node.addEventListener('click', onClick)
    return node
  }

  // Opens the walkthrough's guide section in the Learn more drawer
  // (help_drawer_controller.js, on the same wrapper element).
  learnMore(state, label = 'Learn more') {
    const link = document.createElement('a')
    link.textContent = label
    link.href = state.help
    link.dataset.action = 'help-drawer#open'
    link.dataset.helpUrl = state.help
    if (state.anchor) link.dataset.helpAnchor = state.anchor
    return link
  }
}
